/**
 * Batch Student Modal Component
 *
 * Allows admins to batch add multiple students to a course group.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import {
  listCourseGroupStudents,
  addStudentToCourseGroup
} from '../../api/courseGroupPortalApi';

// Styled components
const Modal = styled(ModalContainer)`
  max-width: 800px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border-radius: 12px 12px 0 0;

  h3 {
    margin: 0;
    font-size: 18px;
    color: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  line-height: 1;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SearchBox = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$color || '#8b5cf6'};
  color: white;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StudentList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  background: ${props => props.$selected ? '#f5f3ff' : 'white'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.$selected ? '#f5f3ff' : '#f9fafb'};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #8b5cf6;
`;

const StudentInfo = styled.div`
  flex: 1;

  .name {
    font-size: 14px;
    color: #1f2937;
    font-weight: 500;
  }

  .meta {
    font-size: 12px;
    color: #6b7280;
  }
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$bg || '#ddd6fe'};
  color: ${props => props.$color || '#5b21b6'};
`;

const SelectedCount = styled.div`
  padding: 12px;
  background: #f5f3ff;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessState = styled.div`
  padding: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  color: #166534;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }
`;

// API for student list
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

const fetchAllStudents = async () => {
  const token = getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE_URL}/student-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation: 'list',
      data: {}
    })
  });

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || 'Failed to fetch students');
  }

  return result.students || [];
};

const BatchStudentModal = ({
  show,
  onClose,
  courseGroup,
  onUpdate
}) => {
  const [allStudents, setAllStudents] = useState([]);
  const [enrolledStudentIds, setEnrolledStudentIds] = useState(new Set());
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  // Settings for new enrollments
  const [defaultCredit, setDefaultCredit] = useState(0);
  const [defaultDiscount, setDefaultDiscount] = useState(0);

  // Load data
  const loadData = useCallback(async () => {
    if (!courseGroup?.id) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const [students, enrollments] = await Promise.all([
        fetchAllStudents(),
        listCourseGroupStudents(courseGroup.id)
      ]);

      setAllStudents(students);
      setEnrolledStudentIds(new Set(enrollments.map(e => e.studentId)));
      setSelectedStudentIds(new Set());

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [courseGroup?.id]);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show, loadData]);

  // Filter students
  const filteredStudents = allStudents.filter(student => {
    // Exclude already enrolled
    if (enrolledStudentIds.has(student.id)) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = (student.name || student.firstName + ' ' + student.lastName).toLowerCase();
      return name.includes(query);
    }

    return true;
  });

  // Toggle student selection
  const toggleStudent = (studentId) => {
    const newSelected = new Set(selectedStudentIds);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudentIds(newSelected);
  };

  // Select all
  const selectAll = () => {
    setSelectedStudentIds(new Set(filteredStudents.map(s => s.id)));
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedStudentIds(new Set());
  };

  // Add selected students
  const handleAddSelected = async () => {
    if (selectedStudentIds.size === 0) {
      alert('Please select at least one student');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    let successCount = 0;
    let failCount = 0;

    const enrollmentStartDate = courseGroup?.startDate || new Date().toISOString().split('T')[0];

    for (const studentId of selectedStudentIds) {
      try {
        await addStudentToCourseGroup({
          courseGroupId: courseGroup.id,
          studentId,
          credit: defaultCredit,
          discount: defaultDiscount,
          startDate: enrollmentStartDate
        });
        successCount++;
      } catch (err) {
        console.error(`Failed to add student ${studentId}:`, err);
        failCount++;
      }
    }

    setSaving(false);

    if (successCount > 0) {
      setSuccess(`Successfully added ${successCount} student(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      await loadData();
      if (onUpdate) onUpdate();
    } else {
      setError(`Failed to add students`);
    }
  };

  if (!show) return null;

  const courseGroupName = courseGroup ? `${courseGroup.title} Y${courseGroup.year}T${courseGroup.term}` : '';

  return (
    <ModalOverlay>
      <Modal>
        <ModalHeader>
          <h3>Batch Add Students - {courseGroupName}</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorState>{error}</ErrorState>}
          {success && <SuccessState>{success}</SuccessState>}

          {loading ? (
            <LoadingState>Loading students...</LoadingState>
          ) : (
            <>
              {/* Default Settings */}
              <Section>
                <SectionTitle>Default Settings for New Enrollments</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <label>Initial Credit</label>
                    <Input
                      type="number"
                      value={defaultCredit}
                      onChange={(e) => setDefaultCredit(parseFloat(e.target.value) || 0)}
                      min="0"
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Discount (%)</label>
                    <Input
                      type="number"
                      value={defaultDiscount}
                      onChange={(e) => setDefaultDiscount(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                    />
                  </FormGroup>
                </FormRow>
              </Section>

              {/* Student Selection */}
              <Section>
                <SectionTitle>Select Students</SectionTitle>

                <SearchBox
                  type="text"
                  placeholder="Search students by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />

                {selectedStudentIds.size > 0 && (
                  <SelectedCount>
                    <span>{selectedStudentIds.size} student(s) selected</span>
                    <Button $color="#6b7280" onClick={deselectAll} style={{ padding: '6px 12px', fontSize: '12px' }}>
                      Clear Selection
                    </Button>
                  </SelectedCount>
                )}

                {filteredStudents.length === 0 ? (
                  <EmptyState>
                    {searchQuery
                      ? 'No students match your search.'
                      : 'All students are already enrolled in this course group.'}
                  </EmptyState>
                ) : (
                  <>
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#6b7280' }}>
                      <button
                        onClick={selectAll}
                        style={{ background: 'none', border: 'none', color: '#8b5cf6', cursor: 'pointer', padding: 0 }}
                      >
                        Select All ({filteredStudents.length})
                      </button>
                    </div>
                    <StudentList>
                      {filteredStudents.map(student => (
                        <StudentRow
                          key={student.id}
                          $selected={selectedStudentIds.has(student.id)}
                          onClick={() => toggleStudent(student.id)}
                        >
                          <Checkbox
                            type="checkbox"
                            checked={selectedStudentIds.has(student.id)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <StudentInfo>
                            <div className="name">
                              {student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unnamed'}
                            </div>
                            <div className="meta">
                              {student.email && <span>{student.email}</span>}
                            </div>
                          </StudentInfo>
                          {student.credit > 0 && (
                            <Badge>Credit: {student.credit}</Badge>
                          )}
                        </StudentRow>
                      ))}
                    </StudentList>
                  </>
                )}
              </Section>
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button $color="#6b7280" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelected}
            disabled={saving || selectedStudentIds.size === 0}
          >
            {saving ? 'Adding...' : `Add ${selectedStudentIds.size} Student(s)`}
          </Button>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};

export default BatchStudentModal;
